// CAD 대시보드 카드를 A4 가로 한 장 이미지로 그린다. 배치는 Card 보기(CadData.js Grid)와 같은 순서다.
// 이미지 저장소가 CORS 허용 헤더를 주지 않아, 브라우저가 직접 받은 이미지는 canvas에서 파일로 꺼낼 수 없다.
// 그래서 이미지는 서버(/api/cad-image)를 거쳐 같은 출처로 받는다.

const WIDTH = 1754; // A4 가로, 150dpi
const HEIGHT = 1240;
const MARGIN = 80;
const GAP = 24;
const FONT = "'Segoe UI', 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif";

const toText = (value) => (value === null || value === undefined ? '' : String(value));

const fitText = (ctx, value, maxWidth) => {
  let text = toText(value);
  if (ctx.measureText(text).width <= maxWidth) return text;
  while (text.length > 0 && ctx.measureText(`${text}…`).width > maxWidth) text = text.slice(0, -1);
  return `${text}…`;
};

// 화면 카드의 justify-content: space-between 줄. 칸이 하나면 가운데 정렬
// 한 줄에 다 들어가면 그대로 두고, 넘칠 때만 칸마다 같은 폭으로 잘라 말줄임을 붙인다
const drawRow = (ctx, y, cells, size, boldFirst = false) => {
  const inner = WIDTH - MARGIN * 2;
  const fonts = cells.map((_, i) => `${boldFirst && i === 0 ? 'bold ' : ''}${size}px ${FONT}`);
  const measure = (list) => list.map((text, i) => {
    ctx.font = fonts[i];
    return ctx.measureText(text).width;
  });

  let texts = cells.map(toText);
  if (measure(texts).reduce((sum, w) => sum + w, 0) + GAP * (cells.length - 1) > inner) {
    const maxEach = (inner - GAP * (cells.length - 1)) / cells.length;
    texts = texts.map((text, i) => {
      ctx.font = fonts[i];
      return fitText(ctx, text, maxEach);
    });
  }
  const widths = measure(texts);

  if (cells.length === 1) {
    ctx.font = fonts[0];
    ctx.fillText(texts[0], (WIDTH - widths[0]) / 2, y);
    return;
  }

  const space = (inner - widths.reduce((sum, w) => sum + w, 0)) / (cells.length - 1);
  let x = MARGIN;
  texts.forEach((text, i) => {
    ctx.font = fonts[i];
    ctx.fillText(text, x, y);
    x += widths[i] + space;
  });
};

const loadImage = async (url) => {
  if (!url || !url.startsWith('http')) return null;
  try {
    const response = await fetch(`/api/cad-image?url=${encodeURIComponent(url)}`);
    if (!response.ok) return null;
    return await createImageBitmap(await response.blob());
  } catch {
    return null;
  }
};

export const drawCadCard = async (item, language) => {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';

  const tag = typeof item.tag === 'number' ? item.tag.toLocaleString() : item.tag;
  const description = language === 'ko' ? item.description : item.description_eng;

  // 위쪽 네 줄
  let y = MARGIN;
  drawRow(ctx, y, [item.part_cd, item.fabric], 40, true);
  y += 64;
  drawRow(ctx, y, [description], 36);
  y += 56;
  drawRow(ctx, y, [item.domain, item.set_up_style_no, item.fit, tag], 30);
  y += 46;
  drawRow(ctx, y, [item.life_style, item.level], 30);
  y += 46 + GAP;

  // 아래쪽 두 줄
  const bottomY = HEIGHT - MARGIN - 46 * 2;
  drawRow(ctx, bottomY, [item.designer, item.vendor], 30);
  drawRow(ctx, bottomY + 46, [item.color], 30);

  // 이미지. 화면과 같이 원본이 없으면 인터스텔라 경로로 한 번 더 찾는다
  const fallbackUrl = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${item.brd_cd}/${item.sesn}/${item.part_cd}.jpg`;
  const image = (await loadImage(item.img)) || (await loadImage(fallbackUrl));
  if (image) {
    const boxWidth = WIDTH - MARGIN * 2;
    const boxHeight = bottomY - GAP - y;
    const scale = Math.min(boxWidth / image.width, boxHeight / image.height);
    const w = image.width * scale;
    const h = image.height * scale;
    ctx.drawImage(image, (WIDTH - w) / 2, y + (boxHeight - h) / 2, w, h);
    image.close();
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('카드 이미지 생성 실패'))), 'image/png');
  });
};

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};

// ponytail: 무압축(store) zip. PNG는 이미 압축돼 있어 충분하다. 4GB를 넘는 묶음(zip64)은 만들지 못한다
export const makeZip = async (files) => {
  const encoder = new TextEncoder();
  const now = new Date();
  const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
  const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
  const locals = [];
  const centrals = [];
  let offset = 0;

  for (const { name, blob } of files) {
    // CRC만 계산하고 바이트 배열은 버린다. 결과 Blob에는 바이트 배열 대신 원본 Blob을 그대로 넣는다
    const crc = crc32(new Uint8Array(await blob.arrayBuffer()));
    const size = blob.size;
    const nameBytes = encoder.encode(name);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true); // 파일 이름 UTF-8
    local.setUint16(10, dosTime, true);
    local.setUint16(12, dosDate, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);
    local.setUint32(22, size, true);
    local.setUint16(26, nameBytes.length, true);
    locals.push(local, nameBytes, blob);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);
    central.setUint16(6, 20, true);
    central.setUint16(8, 0x0800, true);
    central.setUint16(12, dosTime, true);
    central.setUint16(14, dosDate, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, size, true);
    central.setUint32(24, size, true);
    central.setUint16(28, nameBytes.length, true);
    central.setUint32(42, offset, true);
    centrals.push(central, nameBytes);

    offset += 30 + nameBytes.length + size;
  }

  const centralSize = centrals.reduce((sum, part) => sum + part.byteLength, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...locals, ...centrals, end], { type: 'application/zip' });
};
